import { useEffect, useRef, useState, useMemo } from "react";
import { m, AnimatePresence } from "framer-motion";
import type { MentionItem } from "@/types/mention";
import { TIME_OPTIONS } from "@/types/mention";
import type { Project } from "@/types/dashboard";
import type { ConnectedTool } from "@/hooks/useConnectedTools";
import MentionMenuItem from "./MentionMenuItem";

interface MentionMenuProps {
  searchQuery: string;
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
  projects: Project[];
  agents?: ConnectedTool[]; // Connected agents/tools
  recentBranches?: string[]; // Format: "projectName/branchName"
  selectedProjects?: Set<string>; // Already selected project names
  selectedBranches?: Set<string>; // Already selected branch names
  selectedTimeFilter?: string | null; // Already selected time filter
  selectedAgents?: string[]; // Already selected agent IDs
  position?: {
    top?: number;
    left?: number;
    bottom?: string;
    marginBottom?: string;
  };
  isWrapped?: boolean; // Whether the menu is wrapped in a positioning div
}

const MentionMenu = ({
  searchQuery,
  onSelect,
  onClose,
  projects,
  agents = [],
  recentBranches = [],
  selectedProjects = new Set(),
  selectedBranches = new Set(),
  selectedTimeFilter = null,
  selectedAgents = [],
  position,
  isWrapped = false,
}: MentionMenuProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const query = searchQuery.toLowerCase();

  // Filter and prepare mention items using useMemo
  const mentionItems = useMemo(() => {
    const items: MentionItem[] = [];

    // Add projects (exclude already selected ones)
    projects
      .filter((project) => {
        return (
          project.name.toLowerCase().includes(query) &&
          !selectedProjects.has(project.id)
        );
      })
      .forEach((project) => {
        items.push({
          id: `project-${project.id}`,
          type: "project",
          label: project.name,
          value: project.id,
          icon: project.icon,
          color: project.color,
        });
      });

    // Add branches (grouped by project)
    const branchMap = new Map<string, string[]>();
    recentBranches.forEach((branch) => {
      const [projectName, branchName] = branch.split("/");
      if (projectName && branchName) {
        if (!branchMap.has(projectName)) {
          branchMap.set(projectName, []);
        }
        branchMap.get(projectName)?.push(branchName);
      }
    });

    // Filter branches based on query (exclude already selected ones)
    projects
      .filter((project) => {
        const branches = branchMap.get(project.name) || [];
        return branches.some((branch) => {
          const fullBranchName = `${project.name}/${branch}`;
          return (
            !selectedBranches.has(fullBranchName) &&
            (fullBranchName.toLowerCase().includes(query) ||
              branch.toLowerCase().includes(query))
          );
        });
      })
      .forEach((project) => {
        const branches = branchMap.get(project.name) || [];
        branches
          .filter((branch) => {
            const fullBranchName = `${project.name}/${branch}`;
            return (
              !selectedBranches.has(fullBranchName) &&
              (fullBranchName.toLowerCase().includes(query) ||
                branch.toLowerCase().includes(query))
            );
          })
          .forEach((branch) => {
            items.push({
              id: `branch-${project.id}-${branch}`,
              type: "branch",
              label: `${project.name}/${branch}`,
              value: `${project.name}/${branch}`,
              icon: project.icon,
              color: project.color,
              projectName: project.name,
            });
          });
      });

    // Add agents (exclude already selected ones)
    agents
      .filter((agent) => {
        return (
          agent.name.toLowerCase().includes(query) &&
          !selectedAgents.includes(agent.id)
        );
      })
      .forEach((agent) => {
        items.push({
          id: `agent-${agent.id}`,
          type: "agent",
          label: agent.name,
          value: agent.id,
        });
      });

    // Add time options (exclude already selected one)
    TIME_OPTIONS.filter(
      (option) =>
        option.label.toLowerCase().includes(query) &&
        option.value !== selectedTimeFilter
    ).forEach((option) => {
      items.push({
        id: `time-${option.value}`,
        type: "time",
        label: option.label,
        value: option.value,
      });
    });

    return items;
  }, [
    query,
    projects,
    agents,
    recentBranches,
    selectedProjects,
    selectedBranches,
    selectedTimeFilter,
    selectedAgents,
  ]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < mentionItems.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp" || (e.key === "Tab" && e.shiftKey)) {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : mentionItems.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (mentionItems[selectedIndex]) {
          onSelect(mentionItems[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, mentionItems, onSelect, onClose]);

  // Reset selected index when search query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (mentionItems.length === 0) {
    return (
      <AnimatePresence>
        <m.div
          ref={menuRef}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`${!isWrapped ? "absolute bottom-full mb-1" : ""} w-64 bg-popover border border-border rounded-lg shadow-lg z-50 p-3`}
          style={position}
        >
          <div className="text-sm text-muted-foreground text-center">
            No matches found
          </div>
        </m.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <m.div
        ref={menuRef}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className={`${!isWrapped ? "absolute bottom-full mb-1" : ""} w-80 bg-popover border border-border rounded-lg shadow-xl z-50 max-h-[220px] overflow-y-auto`}
        style={position}
      >
        <div className="p-1">
          {mentionItems.map((item, index) => (
            <MentionMenuItem
              key={item.id}
              item={item}
              isSelected={index === selectedIndex}
              onClick={() => onSelect(item)}
              onHover={() => setSelectedIndex(index)}
            />
          ))}
        </div>
      </m.div>
    </AnimatePresence>
  );
};

export default MentionMenu;
